/**
 * The app's one podcast player.
 *
 * A section podcast plays from the Text reader ("Listen") or the Podcast
 * tab, and keeps playing as the student moves between sections, tabs and
 * pages - like the focus timer, it lives above the routes (`app/provider.tsx`)
 * and the Navbar shows it as a pill (`PodcastPillMenu`). There is ONE audio
 * element, so two podcasts never play over each other: the Podcast tab's
 * waveform player attaches to it (`AudioPlayer`'s `media`) instead of making
 * its own.
 *
 * Listening time is recorded here, while it plays, so it counts wherever the
 * student is when they listen.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { track as trackEvent } from '@/config/observability';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { notify } from '@/lib/notify';

export interface PodcastTrack {
  lessonId: string;
  lessonTitle: string;
  sectionIndex: number;
  sectionTitle: string;
  audioUrl: string;
  duration: number;
  peaks: number[];
}

interface PodcastPlayerValue {
  track: PodcastTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  /** The shared element, for a player view to attach to. */
  media: HTMLAudioElement | null;
  /** Load `track` (if it is not the current one) and play it. */
  play: (track: PodcastTrack) => void;
  /** Make `track` the current one without playing, unless something else
   *  is playing - then leave it alone. */
  cue: (track: PodcastTrack) => void;
  toggle: () => void;
  seek: (time: number) => void;
  /** Stop and dismiss the player. */
  close: () => void;
  isCurrent: (lessonId: string, sectionIndex: number) => boolean;
  /** Playback speed (1 = normal). */
  rate: number;
  setRate: (rate: number) => void;
  /** The lesson whose Text reader is on screen: it shows the podcast in the
   *  text itself, so the Navbar pill steps aside for that lesson. */
  readingLesson: string | null;
  setReadingLesson: (lessonId: string | null) => void;
}

const PodcastPlayerContext = createContext<PodcastPlayerValue | null>(null);

function sameTrack(a: PodcastTrack | null, b: PodcastTrack): boolean {
  return !!a && a.lessonId === b.lessonId && a.sectionIndex === b.sectionIndex;
}

/** Play state and position of the element, from its own events. */
function useElementState(audio: HTMLAudioElement | null) {
  const [isPlaying, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);
  useEffect(() => {
    if (!audio) return;
    const onRate = () => setRateState(audio.playbackRate || 1);
    const onPlay = () => setPlaying(!audio.paused);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onDuration = () =>
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onError = () => {
      setPlaying(false);
      // An expired or missing file: the Podcast tab fetches a fresh link.
      if (audio.getAttribute('src')) {
        notify.error("Couldn't play the podcast. Open it again to retry.");
      }
    };
    const handlers: [string, () => void][] = [
      ['play', onPlay],
      ['pause', onPlay],
      ['ended', onPlay],
      ['timeupdate', onTime],
      ['seeked', onTime],
      ['emptied', onTime],
      ['durationchange', onDuration],
      ['ratechange', onRate],
      ['error', onError],
    ];
    handlers.forEach(([e, h]) => audio.addEventListener(e, h));
    return () => handlers.forEach(([e, h]) => audio.removeEventListener(e, h));
  }, [audio]);
  return { isPlaying, currentTime, duration, rate, setCurrentTime };
}

/** `podcast_played` once per section started, `podcast_completed` on end. */
function usePodcastAnalytics(
  audio: HTMLAudioElement | null,
  track: PodcastTrack | null,
  isPlaying: boolean
) {
  const lastPlayed = useRef<string | null>(null);
  useEffect(() => {
    if (!isPlaying || !track) return;
    const key = `${track.lessonId}:${track.sectionIndex}`;
    if (lastPlayed.current === key) return;
    lastPlayed.current = key;
    trackEvent('podcast_played', {
      lesson_id: track.lessonId,
      section_index: track.sectionIndex,
    });
  }, [isPlaying, track]);

  useEffect(() => {
    if (!audio || !track) return;
    const onEnded = () => {
      // Replaying the same section after it ended counts as a new play.
      lastPlayed.current = null;
      trackEvent('podcast_completed', {
        lesson_id: track.lessonId,
        section_index: track.sectionIndex,
      });
    };
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, [audio, track]);
}

export function PodcastPlayerProvider({ children }: { children: ReactNode }) {
  // One element for the life of the app. Not in the DOM: it needs none.
  const [audio] = useState<HTMLAudioElement | null>(() =>
    typeof Audio === 'undefined' ? null : new Audio()
  );
  const [track, setTrack] = useState<PodcastTrack | null>(null);
  const [readingLesson, setReadingLesson] = useState<string | null>(null);
  const { setCurrentTime, ...state } = useElementState(audio);
  usePodcastAnalytics(audio, track, state.isPlaying);

  useActivityTracking({
    contentType: 'podcast',
    lessonId: track?.lessonId,
    enabled: state.isPlaying && !!track,
  });

  // A section already loaded keeps its source (a fresh presigned link for
  // it would restart playback) unless that source failed - e.g. expired.
  const load = useCallback(
    (next: PodcastTrack) => {
      if (!audio) return;
      if (sameTrack(track, next) && !audio.error) return;
      audio.preload = 'metadata';
      audio.src = next.audioUrl;
      setTrack(next);
    },
    [audio, track]
  );

  const value = useMemo<PodcastPlayerValue>(
    () => ({
      track,
      ...state,
      duration: state.duration || track?.duration || 0,
      media: audio,
      play: next => {
        load(next);
        void audio?.play().catch(() => undefined);
      },
      cue: next => {
        if (state.isPlaying && !sameTrack(track, next)) return;
        load(next);
      },
      toggle: () => {
        if (!audio || !track) return;
        if (audio.paused) void audio.play().catch(() => undefined);
        else audio.pause();
      },
      seek: time => {
        if (!audio) return;
        const to = Math.max(0, time);
        audio.currentTime = to;
        // Shown at once: before its metadata loads, the element takes the
        // seek but fires no `timeupdate` for it.
        setCurrentTime(to);
      },
      close: () => {
        if (audio) {
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
        }
        setTrack(null);
      },
      isCurrent: (lessonId, sectionIndex) =>
        !!track &&
        track.lessonId === lessonId &&
        track.sectionIndex === sectionIndex,
      setRate: next => {
        if (audio) audio.playbackRate = next;
      },
      readingLesson,
      setReadingLesson,
    }),
    [audio, track, state, load, setCurrentTime, readingLesson]
  );

  return (
    <PodcastPlayerContext.Provider value={value}>
      {children}
    </PodcastPlayerContext.Provider>
  );
}

/** The player, or null outside a `PodcastPlayerProvider` (a bare story). */
export function useOptionalPodcastPlayer(): PodcastPlayerValue | null {
  return useContext(PodcastPlayerContext);
}
